import { useEffect, useRef, useState } from 'react'
import { Sun, Moon, LogOut, Eye, ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../lib/auth'
import { useOrg } from '../../hooks/useOrg'
import { useRole } from '../../hooks/useRole'
import { usePreviewRole } from '../../hooks/usePreviewRole'
import { useTheme } from '../../hooks/useTheme'
import { useToast } from '../ui/Toast'
import NotificationBell from '../shared/NotificationBell'

const PREVIEW_ROLES = [
  { key: null,         label: 'Super Admin' },
  { key: 'admin',      label: 'Admin' },
  { key: 'member',     label: 'Member' },
  { key: 'foster',     label: 'Foster' },
]

function RoleSwitcher() {
  const { realIsSuperAdmin, role, isPreviewing } = useRole()
  const { previewRole, setPreviewRole } = usePreviewRole()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  if (!realIsSuperAdmin) return null

  const currentLabel =
    isPreviewing
      ? (PREVIEW_ROLES.find(r => r.key === previewRole)?.label || role)
      : 'Super Admin'

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-xs font-semibold text-on-surface bg-surface-container-highest hover:bg-surface-container-high transition-colors"
        title="Preview as another role"
      >
        <Eye size={14} className="text-primary" />
        <span className="hidden sm:inline">Viewing as:</span>
        <span className="text-primary">{currentLabel}</span>
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-44 rounded-md bg-surface-container-lowest overflow-hidden z-30 ghost-border"
          style={{ boxShadow: '0 8px 32px rgba(23,28,31,0.12), 0 2px 8px rgba(23,28,31,0.06)' }}
        >
          {PREVIEW_ROLES.map(r => {
            const active = (r.key || 'super_admin') === role
            return (
              <button
                key={r.label}
                onClick={() => { setPreviewRole(r.key); setOpen(false) }}
                className={`w-full text-left px-3 py-2.5 text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-surface-container text-primary'
                    : 'text-on-surface hover:bg-surface-container-low'
                }`}
              >
                {r.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function TopBar() {
  const { user, profile, signOut } = useAuth()
  const { isImpersonating, stopImpersonating, org } = useOrg()
  const { isPreviewing, role: effectiveRole, realIsSuperAdmin } = useRole()
  const { setPreviewRole } = usePreviewRole()
  const { toggleTheme, isDark } = useTheme()
  const navigate = useNavigate()
  const toast = useToast()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  const ROLE_LABELS = { admin: 'Admin', member: 'Member', foster: 'Foster' }
  const previewLabel = ROLE_LABELS[effectiveRole] || effectiveRole

  useEffect(() => {
    if (!menuOpen) return
    function onClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [menuOpen])

  function handleExitImpersonation() {
    stopImpersonating()
    toast('Exited organization')
    navigate('/platform')
  }

  async function handleSignOut() {
    await signOut()
    navigate('/login', { replace: true })
  }

  const initial = (profile?.display_name || user?.email || 'U')[0].toUpperCase()
  const firstName = (profile?.display_name || user?.email || 'User').split(/[\s@]/)[0]

  return (
    // The shell (AppShell) makes TopBar the first child of a non-scrolling
    // column, so no sticky/fixed positioning is required — TopBar simply
    // sits at the top of the viewport column. `flex-shrink-0` on the root
    // prevents it from collapsing when inner content would overflow.
    <div className="flex-shrink-0 z-40">
      {isImpersonating && (
        <div className="bg-tertiary text-on-tertiary px-4 py-2 text-xs font-semibold flex items-center justify-between tracking-wide">
          <span>Viewing as {org?.name || 'Organization'}</span>
          <button onClick={handleExitImpersonation} className="underline">Exit</button>
        </div>
      )}
      {realIsSuperAdmin && isPreviewing && (
        <div
          className="px-4 py-2 text-xs font-semibold flex items-center justify-between gap-3 tracking-wide"
          style={{ background: '#e8c98a', color: '#5c4320' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Eye size={14} className="flex-shrink-0" />
            <span className="truncate">Previewing as {previewLabel} — actions still run as your super admin account</span>
          </div>
          <button
            onClick={() => setPreviewRole(null)}
            className="underline whitespace-nowrap flex-shrink-0"
          >
            Exit Preview
          </button>
        </div>
      )}

      {/* Mobile header */}
      <header className="frosted-glass px-4 h-14 flex items-center justify-between lg:hidden">
        <img src="/placed-logo.svg" alt="Placed" style={{ height: 32, width: 'auto', display: 'block' }} />
        <div className="flex items-center gap-2">
          <RoleSwitcher />
          <NotificationBell />
          <button
            onClick={toggleTheme}
            className="w-9 h-9 rounded-full flex items-center justify-center text-on-surface-variant hover:bg-surface-container-high transition-colors"
            aria-label="Toggle dark mode"
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          {user && (
            <button
              onClick={handleSignOut}
              className="w-9 h-9 rounded-full bg-primary text-on-primary text-xs font-bold flex items-center justify-center"
              aria-label="Sign out"
            >
              {initial}
            </button>
          )}
        </div>
      </header>

      {/* Desktop header */}
      <header className="hidden lg:flex h-16 items-center justify-end px-8 bg-surface-container-low/80 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <RoleSwitcher />
          <NotificationBell />
          <button
            onClick={toggleTheme}
            className="w-9 h-9 rounded-full flex items-center justify-center text-on-surface-variant hover:bg-surface-container-high transition-colors"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun size={18} strokeWidth={1.5} /> : <Moon size={18} strokeWidth={1.5} />}
          </button>
          {user && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(o => !o)}
                className="w-9 h-9 rounded-full bg-primary text-on-primary text-xs font-bold flex items-center justify-center hover:opacity-90 transition-opacity"
                aria-label="User menu"
              >
                {initial}
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-48 rounded-md bg-surface-container-lowest overflow-hidden"
                  style={{ boxShadow: '0 8px 32px rgba(23,28,31,0.12), 0 2px 8px rgba(23,28,31,0.06)' }}
                >
                  <div className="px-4 py-3">
                    <p className="text-sm font-semibold text-on-surface truncate">{firstName}</p>
                    <p className="text-xs text-on-surface-variant truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low transition-colors"
                  >
                    <LogOut size={16} strokeWidth={1.5} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
    </div>
  )
}
